import moment from "moment";
import { useEffect } from "react";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { BtnComponent } from "../../components/button";
import { PageDialogContent, PageDialogFooter } from "../../components/dialog";
import SpeechMic from "../../components/mic";
import { DatePickerFeild } from "../../components/reactHookFormFields/datePicker";
import { InputField } from "../../components/reactHookFormFields/inputField";
import { ReactSelectField } from "../../components/reactHookFormFields/selectField";
import { TextAreaField } from "../../components/reactHookFormFields/textAreaFields";
import { useGroupChatContext } from "../../context/groupchatContext";
import { useAddConsentMutation, useGetReferralsMetaQuery } from "../../services/referralService";


const NursingConsent = ({data, cancelNursingConsentPopup}) => {

    const givenByOptions = [
        {label: 'Patient', key: 'patient'},
        {label: 'Support Contact', key: 'support_contact'},
        {label: 'No Consent Available', key: 'no_consent'}
    ]

    const {data: referralsMeta} = useGetReferralsMetaQuery();

    const groupChatContext = useGroupChatContext();
    const {selectedChat} = useMemo(() => ({
        selectedChat: groupChatContext.selectedChat,
    }));

    const {
        handleSubmit,
        control,
        reset,
        getValues,
        setValue,
        formState: { errors },
    } = useForm();

    const takenFromVal = useWatch({control, name: 'taken_from'});


    useEffect(()=>{
        if(data?.id){
            const foundGivenBy = givenByOptions.find((item)=> item.key == data.taken_from);
            const foundContactType = referralsMeta?.contact_types.find((item)=> item.title == data.support_contact_type);
            const foundRelationShip = referralsMeta?.relationship.find((item)=> item.title == data.support_contact_relationship);
            let optionObjData = {};
            if(foundContactType){
                optionObjData.support_contact_type = {label: foundContactType?.title, ...foundContactType}
            }
            if(foundRelationShip){
                optionObjData.support_contact_relationship = {label: foundRelationShip?.title, ...foundRelationShip }
            }

            reset({...data, taken_from: foundGivenBy, ...optionObjData});
        }
    }, [])

    const onSpeechAvailable = (text) =>{
        const descriptionVal = getValues('description');
        const finalText = ((descriptionVal ? descriptionVal : '')+' '+text).trim()
        setValue('description', finalText)
    }

    const [addConsent, {isLoading}] = useAddConsentMutation();

    const onSubmitForm = async(values) => {
        
        const payload = {
            pid: selectedChat?.pid,
            start_date: moment(values.start_date).format('YYYY-MM-DD'),
            taken_from: values.taken_from?.key, description: values.description,
            taken_by_external_user_name: values.taken_by_external_user_name,
            support_contact_type: values.support_contact_type?.title,
            support_contact_relationship: values.support_contact_relationship?.title,
            support_contact_name: values.support_contact_name,
        };
        
        if(data?.id){
            payload.id = data.id;
        }

        const res = await addConsent(payload);
        if(res?.data?.code === 0){
            toast.success(res.data.msg);
            cancelNursingConsentPopup(res.data.data);
        }
        else {
            toast.error(res?.data.msg);
        }
    }

    return(
        <>
            <PageDialogContent>
                <form id="nursing_consent_form" onSubmit={handleSubmit(onSubmitForm)}>
                    <div className='flex gap-x-4 mb-3'>
                        <div className="w-1/3">
                            <DatePickerFeild
                                title="Date"
                                control={control}
                                name="start_date"
                                dateTime={false}
                                rules={{
                                    required: {value: true},
                                }}
                                errors={errors}
                            />
                        </div>
                        <div className='w-1/3'>
                            <ReactSelectField
                                title="Given By"
                                name="taken_from"
                                control={control}
                                optionData={givenByOptions}
                                rules={{
                                    required: {value: true},
                                }}
                                errors={errors}
                                menuPlacement={"bottom"}
                            />
                        </div>
                        <div className='w-1/3'>
                            <InputField
                                title="Obtained By"
                                name={'taken_by_external_user_name'}
                                control={control}
                            />
                        </div>
                    </div>
                    {takenFromVal?.key == 'support_contact' ? 
                        <div className='flex gap-x-4 mb-3'>
                            <div className='w-1/3'>
                                <ReactSelectField
                                    title="Contact Type"
                                    name="support_contact_type"
                                    control={control}
                                    optionData={referralsMeta?.contact_types?.map((v, k) => ({
                                        ...v,
                                        label: v.title,
                                    }))}
                                    rules={{
                                        required: {value: true},
                                    }}
                                    errors={errors}
                                    menuPlacement={"bottom"}
                                />
                            </div>
                            <div className='w-1/3'>
                                <ReactSelectField
                                    title="Relation"
                                    name="support_contact_relationship"
                                    control={control}
                                    optionData={referralsMeta?.relationship?.map((v, k) => ({
                                        ...v,
                                        label: v.title,
                                    }))}
                                    rules={{
                                        required: {value: true},
                                    }}
                                    errors={errors}
                                    menuPlacement={"bottom"}
                                />
                            </div>
                            <div className='w-1/3'>
                                <InputField
                                    title="Name"
                                    name='support_contact_name'
                                    control={control}
                                    rules={{
                                        required: {value: true},
                                    }}
                                    errors={errors}
                                />
                            </div>
                        </div>
                        : null
                    }
                    <div className="relative">
                        <SpeechMic transformSpeech={onSpeechAvailable} />
                        <TextAreaField
                            name="description"
                            title="Description"
                            rows="4"
                            control={control}
                        />
                    </div>
                </form>
            </PageDialogContent>
            <PageDialogFooter>
                <BtnComponent text="Cancel" btnType="default" onClick={cancelNursingConsentPopup} />
                <BtnComponent text="Save" btnType='primary_theme' loading={isLoading} form="nursing_consent_form" type="submit" />
            </PageDialogFooter>
        </>
    )
}

export default NursingConsent;